import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface SectionStyle {
  type: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

const defaultStyles: SectionStyle[] = [
  {
    type: 'scripture',
    backgroundColor: 'bg-crimson/10',
    textColor: 'text-crimson',
    borderColor: 'border-crimson'
  },
  {
    type: 'illustration',
    backgroundColor: 'bg-emerald/10',
    textColor: 'text-emerald', 
    borderColor: 'border-emerald'
  },
  {
    type: 'quote',
    backgroundColor: 'bg-electric/10',
    textColor: 'text-electric',
    borderColor: 'border-electric'
  },
  {
    type: 'application',
    backgroundColor: 'bg-gold/10',
    textColor: 'text-gold',
    borderColor: 'border-gold'
  },
  {
    type: 'main-point',
    backgroundColor: 'bg-fiery/10',
    textColor: 'text-fiery',
    borderColor: 'border-fiery'
  },
  {
    type: 'custom',
    backgroundColor: 'bg-slate/10',
    textColor: 'text-slate',
    borderColor: 'border-slate'
  }
];

export function useSectionStyles() {
  const [styles, setStyles] = useState<SectionStyle[]>(() => {
    const saved = localStorage.getItem('sermon_section_styles');
    const loadedStyles = saved ? JSON.parse(saved) : defaultStyles;
    
    // Ensure custom section is always included
    const hasCustom = loadedStyles.some((style: SectionStyle) => style.type === 'custom');
    if (!hasCustom) {
      loadedStyles.push({
        type: 'custom',
        backgroundColor: 'bg-slate/10',
        textColor: 'text-slate',
        borderColor: 'border-slate'
      });
    }
    
    return loadedStyles;
  });

  // Load default profile on mount
  useEffect(() => {
    const loadDefaultProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error } = await supabase
          .from('style_profiles')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_default', true)
          .maybeSingle(); // Use maybeSingle instead of single to handle no results

        // Only update styles if we found a default profile
        if (!error && profile?.styles) {
          setStyles(profile.styles);
          localStorage.setItem('sermon_section_styles', JSON.stringify(profile.styles));
        }
      } catch (err) {
        console.error('Error loading default style profile:', err);
        // Fallback to default styles if there's an error
        setStyles(defaultStyles);
        localStorage.setItem('sermon_section_styles', JSON.stringify(defaultStyles));
      }
    };

    loadDefaultProfile();
  }, []);

  // Save styles to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('sermon_section_styles', JSON.stringify(styles));
  }, [styles]);

  const updateStyle = (type: string, updates: Partial<SectionStyle>) => {
    setStyles(prev => 
      prev.map(style => 
        style.type === type 
          ? { ...style, ...updates }
          : style
      )
    );
  };

  const resetStyles = () => {
    setStyles(defaultStyles);
    localStorage.setItem('sermon_section_styles', JSON.stringify(defaultStyles));
  };

  return {
    styles,
    updateStyle,
    resetStyles
  };
}